import { createContext, use, useSyncExternalStore } from 'react';

/**
 * The columns the signed-in person has hidden, for every table with an `id`
 * (#444).
 *
 * Kept on the server -- nothing in this application writes to browser storage
 * (see `session/sessionStore.ts`) -- and sent with the principal as
 * `hidden_columns`. That copy is only where this store starts: from then on
 * every toggle lands here first and goes to the server after, so a second
 * screen showing the same table sees the choice at once, before the server
 * has answered, and a write that fails costs the next session the preference,
 * not this one.
 *
 * A plain module rather than React state for the reason the session is one:
 * it outlives every screen that reads it, and one copy cannot drift from
 * another.
 */
export interface HiddenColumnsSource {
  /** Whose preferences these are. Someone else signing in starts over. */
  readonly owner: string;
  /** Hidden column keys by table id, as the server sent them on the principal. */
  readonly stored: Readonly<Record<string, readonly string[]>> | undefined;
  /** Write one table's hidden keys through to the server. Nothing waits for it. */
  readonly remember: (tableId: string, hidden: readonly string[]) => void;
}

/**
 * Supplied once, around the signed-in application. A table rendered outside
 * it -- or without an `id` -- keeps its column choices in memory, for as long
 * as it is mounted, and writes nothing.
 */
export const HiddenColumnsContext = createContext<HiddenColumnsSource | null>(null);

const NOTHING_HIDDEN: readonly string[] = [];

let owner: string | null = null;
let byTable: ReadonlyMap<string, readonly string[]> = new Map();
const listeners = new Set<() => void>();

/** Start from the server's copy the first time a person's preferences are read. */
function seed(source: HiddenColumnsSource): void {
  if (owner === source.owner) return;
  owner = source.owner;
  byTable = new Map(Object.entries(source.stored ?? {}));
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Drop everything held, as a session ends. The next one starts from its principal. */
export function forgetHiddenColumns(): void {
  owner = null;
  byTable = new Map();
}

/** One table's stored choice, and the way to change it. */
export interface StoredHiddenColumns {
  /** Unfiltered: may name columns the table no longer has, or may not hide. */
  readonly hidden: readonly string[];
  /** Record a new choice here, then on the server. An empty list clears it. */
  readonly store: (hidden: readonly string[]) => void;
}

/**
 * The stored choice for `tableId`, or `null` when the table has no `id` or no
 * source is supplied -- its visibility is then its own business.
 */
export function useStoredHiddenColumns(tableId: string | undefined): StoredHiddenColumns | null {
  const source = use(HiddenColumnsContext);
  const persisted = tableId !== undefined && source !== null;
  const read = () => {
    if (!persisted) return NOTHING_HIDDEN;
    seed(source);
    return byTable.get(tableId) ?? NOTHING_HIDDEN;
  };
  const hidden = useSyncExternalStore(subscribe, read, read);
  if (!persisted) return null;
  return {
    hidden,
    store: (next) => {
      seed(source);
      const updated = new Map(byTable);
      if (next.length === 0) updated.delete(tableId);
      else updated.set(tableId, next);
      byTable = updated;
      for (const listener of listeners) listener();
      source.remember(tableId, next);
    },
  };
}
